import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface PayPalCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  tierName: string;
  billingCycle: string;
  price: string;
}

type Step = 'auth' | 'paying' | 'success' | 'error';

const EDGE_FN = 'https://bjgybptlkbqvgrzzkdxh.supabase.co/functions/v1/paypal-checkout';
const PAYPAL_SDK = 'https://www.paypal.com/sdk/js';

declare global {
  interface Window {
    paypal?: any;
  }
}

export default function PayPalCheckoutModal({
  isOpen,
  onClose,
  tierName,
  billingCycle,
  price,
}: PayPalCheckoutModalProps) {
  const [step, setStep] = useState<Step>('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  const paypalRendered = useRef(false);

  // Check if already logged in
  useEffect(() => {
    if (!isOpen) return;
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) {
        setUserId(data.session.user.id);
        setUserEmail(data.session.user.email ?? '');
        setStep('paying');
      } else {
        setStep('auth');
      }
    });
  }, [isOpen]);

  // Load PayPal SDK
  useEffect(() => {
    if (step !== 'paying') return;
    if (window.paypal) { setSdkReady(true); return; }
    const existing = document.getElementById('paypal-sdk');
    if (existing) { setSdkReady(true); return; }

    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `${PAYPAL_SDK}?client-id=${import.meta.env.VITE_PAYPAL_CLIENT_ID ?? 'sb'}&currency=USD`;
    script.onload = () => setSdkReady(true);
    document.body.appendChild(script);
  }, [step]);

  // Render PayPal buttons
  useEffect(() => {
    if (!sdkReady || step !== 'paying' || !paypalContainerRef.current) return;
    if (paypalRendered.current) return;
    paypalRendered.current = true;

    const numericPrice = price.replace(/[^0-9.]/g, '');

    window.paypal
      .Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' },
        createOrder: async () => {
          const res = await fetch(EDGE_FN, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'create_order',
              amount: numericPrice,
              tierName,
              billingCycle,
              userId,
              userEmail,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error ?? 'Failed to create order');
          return data.orderId;
        },
        onApprove: async (_data: any, actions: any) => {
          const orderId = _data.orderID;
          const res = await fetch(EDGE_FN, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'capture_order',
              orderId,
              tierName,
              billingCycle,
              userId,
              userEmail,
            }),
          });
          const result = await res.json();
          if (!res.ok || !result.success) {
            setErrorMsg(result.error ?? 'Payment capture failed.');
            setStep('error');
            return;
          }
          setStep('success');
        },
        onError: (err: any) => {
          console.error(err);
          setErrorMsg('PayPal encountered an error. Please try again.');
          setStep('error');
        },
        onCancel: () => {
          // user cancelled — stay on paying step
        },
      })
      .render(paypalContainerRef.current);
  }, [sdkReady, step]);

  // Reset on close
  const handleClose = () => {
    paypalRendered.current = false;
    setSdkReady(false);
    setEmail('');
    setPassword('');
    setAuthError('');
    setErrorMsg('');
    onClose();
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      let result;
      if (isLogin) {
        result = await supabase.auth.signInWithPassword({ email, password });
      } else {
        result = await supabase.auth.signUp({ email, password });
      }
      if (result.error) throw result.error;
      const user = result.data.user ?? result.data.session?.user;
      if (!user) throw new Error('No user returned. Check your email to confirm your account.');
      setUserId(user.id);
      setUserEmail(user.email ?? email);
      setStep('paying');
    } catch (err: any) {
      setAuthError(err.message ?? 'Authentication failed.');
    } finally {
      setAuthLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="flex items-center gap-3 mb-1">
            <i className="ri-vip-crown-line text-2xl"></i>
            <h2 className="text-xl font-bold">Complete Your Purchase</h2>
          </div>
          <p className="text-emerald-100 text-sm">
            {tierName} &mdash; {billingCycle} &mdash; <strong>{price}</strong>
          </p>
        </div>

        <div className="p-6">
          {/* ── STEP: AUTH ── */}
          {step === 'auth' && (
            <div>
              <p className="text-gray-600 text-sm mb-5">
                {isLogin ? 'Sign in to your account to continue.' : 'Create a free account to get started.'}
              </p>
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="••••••••"
                  />
                </div>
                {authError && (
                  <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{authError}</p>
                )}
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {authLoading ? 'Please wait…' : isLogin ? 'Sign In & Continue' : 'Create Account & Continue'}
                </button>
              </form>
              <p className="text-center text-sm text-gray-500 mt-4">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
                <button
                  onClick={() => { setIsLogin(!isLogin); setAuthError(''); }}
                  className="text-emerald-600 font-semibold hover:underline cursor-pointer"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>
          )}

          {/* ── STEP: PAYING ── */}
          {step === 'paying' && (
            <div>
              <div className="flex items-center gap-2 mb-4 bg-emerald-50 px-4 py-3 rounded-lg">
                <i className="ri-user-line text-emerald-600"></i>
                <span className="text-sm text-emerald-800 font-medium">{userEmail}</span>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex justify-between items-center text-sm text-gray-700 mb-1">
                  <span>{tierName}</span>
                  <span className="font-bold text-gray-900">{price}</span>
                </div>
                <div className="text-xs text-gray-500">{billingCycle} billing</div>
              </div>
              {!sdkReady && (
                <div className="flex items-center justify-center py-8 gap-3 text-gray-500">
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  <span className="text-sm">Loading payment…</span>
                </div>
              )}
              <div ref={paypalContainerRef} id="paypal-button-container" />
              <p className="text-center text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
                <i className="ri-lock-line"></i> Secured by PayPal
              </p>
            </div>
          )}

          {/* ── STEP: SUCCESS ── */}
          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 text-sm mb-1">
                Your <strong>{tierName}</strong> membership is now <span className="text-emerald-600 font-bold">ACTIVE</span>.
              </p>
              <p className="text-gray-500 text-xs mb-6">Welcome aboard! You now have full access to your plan.</p>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              >
                Start Learning
              </button>
            </div>
          )}

          {/* ── STEP: ERROR ── */}
          {step === 'error' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h3>
              <p className="text-gray-600 text-sm mb-6">{errorMsg}</p>
              <button
                onClick={() => { paypalRendered.current = false; setSdkReady(false); setStep('paying'); }}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
